/**
 * Shared vocabulary every screen draws from, so option lists/icons/copy stay
 * identical wherever they appear (task form, manage filters, skip screen).
 * Values match backend enums exactly — see ETM_Tasks::VALID_SKIP_REASONS
 * and the roadmap §4 schema.
 *
 * Icons are Material Symbols names rendered by components/Icon.jsx (WP-1) —
 * emoji glyphs are gone from the UI. The `icon` DB column now stores these
 * names for new/edited categories; LEGACY_CATEGORY_EMOJI_ICONS below keeps
 * every value the old emoji picker could have written rendering correctly
 * without any data migration.
 */
import { __ } from '../i18n.js';

export const SKIP_REASONS = [
  { value: 'materials', icon: 'construction', label: () => __('Mis gereedschap/materiaal', 'etm') },
  { value: 'help', icon: 'group', label: () => __('Heb hulp nodig', 'etm') },
  { value: 'money', icon: 'payments', label: () => __('Te duur nu', 'etm') },
  { value: 'weather', icon: 'rainy', label: () => __('Weer/omstandigheden', 'etm') },
  { value: 'mood', icon: 'bedtime', label: () => __('Geen zin', 'etm') },
  { value: 'time', icon: 'schedule', label: () => __('Toch geen tijd', 'etm') },
  { value: 'other', icon: 'help', label: () => __('Iets anders', 'etm') },
];

export function skipReasonLabel(value) {
  const found = SKIP_REASONS.find((r) => r.value === value);
  return found ? found.label() : value;
}

export const TIME_PRESETS = [
  { minutes: 15, icon: 'bolt', label: () => __('Quick win', 'etm'), sublabel: () => __('15 min', 'etm') },
  { minutes: 60, icon: 'local_cafe', label: () => __('Uurtje', 'etm'), sublabel: () => __('1 uur', 'etm') },
  { minutes: 240, icon: 'partly_cloudy_day', label: () => __('Halve dag', 'etm'), sublabel: () => __('4 uur', 'etm') },
  { minutes: 480, icon: 'calendar_month', label: () => __('Hele dag', 'etm'), sublabel: () => __('8 uur', 'etm') },
];

export const IMPACT_OPTIONS = [
  { value: 1, label: () => __('Lage impact', 'etm') },
  { value: 2, label: () => __('Normale impact', 'etm') },
  { value: 3, label: () => __('Hoge impact', 'etm') },
];

export const ENERGY_OPTIONS = [
  { value: 'low', label: () => __('Weinig energie nodig', 'etm') },
  { value: 'normal', label: () => __('Normaal', 'etm') },
  { value: 'high', label: () => __('Veel energie nodig', 'etm') },
];

// Moved here from TaskFormScreen (WP-2): the new UpcomingPanel's priority
// indicator needs the same 1-4 scale/labels, so it's shared vocabulary now
// rather than a screen-local const.
export const PRIORITY_LEVELS = [
  { value: 1, label: () => __('Laag', 'etm') },
  { value: 2, label: () => __('Normaal', 'etm') },
  { value: 3, label: () => __('Hoog', 'etm') },
  { value: 4, label: () => __('Urgent', 'etm') },
];

/**
 * Curated category icon set (WP-1 brief): Material Symbols names offered by
 * the category editor and stored as-is in the `icon` column
 * (ETM_Categories sanitizes with sanitize_text_field(), which passes these
 * plain names through unchanged — verified, not assumed).
 */
export const CATEGORY_ICON_CHOICES = [
  'person',
  'home',
  'work',
  'potted_plant',
  'shopping_cart',
  'fitness_center',
  'menu_book',
  'directions_car',
  'lightbulb',
  'palette',
  'favorite',
  'category',
];

/** Neutral fallback for empty/unknown stored icon values. */
export const CATEGORY_FALLBACK_ICON = 'category';

// Every emoji the pre-WP-1 picker offered (plus the seeded defaults), mapped
// to its Material Symbols replacement so existing users' categories keep
// rendering without touching their rows. ❤ is listed both with and without
// the VS16 variation selector — the old picker stored the VS16 form, but a
// hand-edited row shouldn't fall through to the fallback over an invisible
// codepoint.
const LEGACY_CATEGORY_EMOJI_ICONS = {
  '📦': 'category',
  '💼': 'work',
  '🏠': 'home',
  '👤': 'person',
  '🌱': 'potted_plant',
  '🛒': 'shopping_cart',
  '💪': 'fitness_center',
  '📚': 'menu_book',
  '🚗': 'directions_car',
  '💡': 'lightbulb',
  '🎨': 'palette',
  '❤️': 'favorite',
  '❤': 'favorite',
};

/**
 * Stored `icon` column value (icon name, legacy emoji, or empty) → the
 * Material Symbols name to render. Unknown values (a future icon name this
 * bundle doesn't know, a hand-edited row) get the neutral fallback rather
 * than a broken glyph.
 */
export function categoryIconName(stored) {
  if (!stored) return CATEGORY_FALLBACK_ICON;
  if (CATEGORY_ICON_CHOICES.includes(stored)) return stored;
  return LEGACY_CATEGORY_EMOJI_ICONS[stored] || CATEGORY_FALLBACK_ICON;
}
